import { AccountAddress } from "@aptos-labs/ts-sdk";
import { sha256 } from "@noble/hashes/sha2.js";

import { toChecksumAddress } from "./eip55";
import { isEvmAddress } from "./evm";

/**
 * Address validation, per chain.
 *
 * One address in the cross-chain integration is supplied by a person rather than by us: the
 * `recipient` on a withdrawal, typed into the withdraw modal. A deposit hands the user an address
 * instead of asking for one, and `refundTo` on a withdrawal is the session's own Aptos account.
 *
 * Validated here rather than left to 1Click's `recipient is not valid` because the address is baked
 * permanently into a quote and cannot be edited afterwards: a bad recipient is a withdrawal sent
 * somewhere nobody controls.
 *
 * Shared between the server and the web app deliberately. The server is the boundary that actually
 * enforces this — a client check can be bypassed — but the client runs it as the user types,
 * because by the time the server sees the address the modal has already asked for a signature. Two
 * copies of a checksum are two chances to diverge, and the one that drifts is the one nobody tested.
 */

/**
 * The chains an address can belong to, classified by address format.
 *
 * A withdrawal checks the address the user typed against its route's kind. A deposit never asks the
 * user for one; it only uses `detectChainKind` to tell which chain a deposit address it was handed
 * is on.
 */
export const CHAIN_KINDS = ["solana", "evm", "near", "bitcoin", "xrp", "aptos"] as const;

export type ChainKind = (typeof CHAIN_KINDS)[number];

/**
 * Whether this build knows how to validate addresses for a chain kind.
 *
 * The withdraw allowlist is server config — `chainKind` crosses the wire as a plain string so that
 * enabling a chain does not require a frontend deploy. A client handed a kind it has never heard of
 * cannot check the address and must not guess: it defers to the server, which is the build that
 * added the route.
 */
export function isChainKind(value: string): value is ChainKind {
  return (CHAIN_KINDS as readonly string[]).includes(value);
}

/**
 * Why an address was rejected, for a caller that has to say so in the user's own language.
 *
 * The messages below are English and written for a user, which is right for an API response and
 * wrong for the modal. The web app maps these codes onto its own translated strings; anything it
 * does not recognise falls back to the message.
 */
export type ChainAddressErrorCode =
  | "empty"
  | "solanaShape"
  | "nearShape"
  | "evmShape"
  | "evmChecksum"
  | "bitcoinShape"
  | "bitcoinChecksum"
  | "xrpShape"
  | "xrpChecksum"
  | "aptosShape";

/**
 * Base58, Solana's length bound. The alphabet is Bitcoin's — it omits 0, O, I and l as visually
 * confusable — but this must not be reused for Bitcoin: legacy `1…`/`3…` addresses carry a
 * Base58Check checksum, which `decodeBase58` below verifies rather than shape-matching.
 */
const BASE58_ADDRESS = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
/**
 * NEAR account ids: dot-separated parts of lowercase alphanumerics, with `-` and `_` allowed
 * between characters but never at the edge of a part. An implicit account (64 lowercase hex) is
 * already covered by this shape. Length is checked separately — NEAR's own bound is 2-64.
 */
const NEAR_ACCOUNT_ID = /^(([a-z\d]+[-_])*[a-z\d]+\.)*([a-z\d]+[-_])*[a-z\d]+$/;
const NEAR_MIN_LENGTH = 2;
const NEAR_MAX_LENGTH = 64;

/** Bech32 (BIP-173) and bech32m (BIP-350), mainnet only. */
const BECH32_CHARSET = "qpzry9x8gf2tvdw0s3jn54khce6mua7l";
const BITCOIN_HRP = "bc";
const BECH32_CHECKSUM_LENGTH = 6;
/** BIP-173 caps the whole address here, and the polymod below assumes a bounded input. */
const BECH32_MAX_LENGTH = 90;
/** Native-segwit Bitcoin (`bc1…`, bech32 or bech32m): checks the charset, not length or checksum. */
const BITCOIN_SEGWIT_SHAPE = /^bc1[02-9ac-hj-np-z]+$/i;
const BECH32_CONST = 1;
const BECH32M_CONST = 0x2bc830a3;

const BASE58_ALPHABET = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";
const XRP_BASE58_ALPHABET = "rpshnaf39wBUDNEGHJKLM4PQRST7VWXYZ2bcdeCg65jkm8oFqi1tuvAxyz";
/** One version byte, a 20-byte hash, and four checksum bytes. */
const BASE58CHECK_LENGTH = 25;
const BASE58CHECK_CHECKSUM_LENGTH = 4;
/** Mainnet P2PKH (`1…`) and P2SH (`3…`). Testnet's 0x6f / 0xc4 are deliberately absent. */
const BITCOIN_P2PKH_VERSION = 0x00;
const BITCOIN_P2SH_VERSION = 0x05;

export class InvalidChainAddressError extends Error {
  readonly code: ChainAddressErrorCode;
  constructor(code: ChainAddressErrorCode, message: string) {
    super(message);
    this.name = "InvalidChainAddressError";
    this.code = code;
  }
}

/**
 * Validate an address for a chain and return it in canonical form.
 *
 * EVM addresses are returned EIP-55 checksummed. A single-case address carries no checksum, so
 * it is checksummed for the caller; a mixed-case address asserts its own checksum and is
 * rejected if it fails, since that is what a mistyped character looks like.
 */
export function normalizeChainAddress(chainKind: ChainKind, raw: string): string {
  const address = raw.trim();

  if (address.length === 0) {
    throw new InvalidChainAddressError("empty", "Enter an address.");
  }

  // Exhaustive rather than `if solana … else EVM`: a chain kind added to the allowlist without a
  // case here must fail to compile, not silently get validated as an EVM address.
  switch (chainKind) {
    case "solana":
      if (!BASE58_ADDRESS.test(address)) {
        throw new InvalidChainAddressError(
          "solanaShape",
          "Enter a valid Solana address (32-44 base58 characters).",
        );
      }
      return address;

    case "near":
      if (
        address.length < NEAR_MIN_LENGTH ||
        address.length > NEAR_MAX_LENGTH ||
        !NEAR_ACCOUNT_ID.test(address)
      ) {
        throw new InvalidChainAddressError(
          "nearShape",
          "Enter a valid NEAR account id (2-64 lowercase characters, e.g. name.near).",
        );
      }
      // No canonicalisation: NEAR ids are lowercase-only, so anything else is a different account
      // rather than a different spelling of this one.
      return address;

    case "evm":
      return normalizeEvmAddress(address);

    case "bitcoin":
      return normalizeBitcoinAddress(address);

    case "xrp":
      return normalizeXrpAddress(address);

    case "aptos":
      return normalizeAptosAddress(address);
  }
}

/**
 * Why an address is invalid for a chain, or `undefined` if it is fine.
 *
 * For a form that has to decide whether to enable a button, where the canonical form is the
 * server's business and only the reason matters.
 */
export function chainAddressError(
  chainKind: ChainKind,
  raw: string,
): InvalidChainAddressError | undefined {
  try {
    normalizeChainAddress(chainKind, raw);
    return undefined;
  } catch (error) {
    // Rethrown rather than swallowed: `normalizeChainAddress` throws nothing else, so anything
    // else is a bug in it and reporting it as "your address is invalid" would hide that.
    if (error instanceof InvalidChainAddressError) return error;
    throw error;
  }
}

/**
 * EVM, segwit Bitcoin or Solana by shape alone, checksums unchecked; null otherwise. Any base58 of
 * Solana's length reads as Solana, so a caller that knows another base58 chain checks it first.
 */
export function detectChainKind(raw: string): ChainKind | null {
  const address = raw.trim();
  if (isEvmAddress(address)) return "evm";
  if (BITCOIN_SEGWIT_SHAPE.test(address)) return "bitcoin";
  if (BASE58_ADDRESS.test(address)) return "solana";
  return null;
}

/**
 * Validate an Aptos address and return it in the canonical long lowercase form.
 *
 * Parsed strictly, unlike `canonicalAptosAddress` in the server's `utils/crypto.ts`. That one is
 * for addresses the chain gave us, where AIP-40's relaxed rules are the point: the JSON RPC trims
 * leading zero bytes, so a short form has to be padded back out. Here the address came from a
 * person typing or pasting into a form, and padding is the failure mode rather than the fix — a
 * paste that dropped its last character parses as a different, entirely valid address, and the
 * withdrawal sent to it is gone. Strict parsing accepts only the 64-character form (plus the
 * reserved 0x0-0xf).
 */
function normalizeAptosAddress(address: string): string {
  try {
    return AccountAddress.fromStringStrict(address).toStringLong().toLowerCase();
  } catch {
    throw new InvalidChainAddressError(
      "aptosShape",
      "Enter a valid Aptos address (0x followed by 64 hex characters).",
    );
  }
}

function normalizeEvmAddress(address: string): string {
  if (!isEvmAddress(address)) {
    throw new InvalidChainAddressError(
      "evmShape",
      "Enter a valid Ethereum-style address (0x followed by 40 hex characters).",
    );
  }

  const body = address.slice(2);
  const isSingleCase = body === body.toLowerCase() || body === body.toUpperCase();
  const checksummed = toChecksumAddress(`0x${body.toLowerCase()}`);

  // oxlint-disable-next-line custom/no-raw-address-comparison -- EIP-55 checksum match; Aptos canonicalization does not apply
  if (!isSingleCase && address !== checksummed) {
    throw new InvalidChainAddressError(
      "evmChecksum",
      "That address failed its EIP-55 checksum. Re-copy it from your wallet.",
    );
  }

  return checksummed;
}

/** BIP-173's checksum over the expanded human-readable part plus the data part. */
function bech32Polymod(values: number[]): number {
  const generator = [0x3b6a57b2, 0x26508e6d, 0x1ea119fa, 0x3d4233dd, 0x2a1462b3];
  let chk = 1;
  for (const value of values) {
    const top = chk >>> 25;
    chk = ((chk & 0x1ffffff) << 5) ^ value;
    for (const [i, bit] of generator.entries()) {
      if ((top >>> i) & 1) chk ^= bit;
    }
  }
  return chk >>> 0;
}

function expandHrp(hrp: string): number[] {
  const high: number[] = [];
  const low: number[] = [];
  // Indexed rather than iterated: the hrp is ASCII by definition, and iterating a string yields
  // code points, which is both slower and wrong for anything outside it.
  for (let i = 0; i < hrp.length; i += 1) {
    const code = hrp.charCodeAt(i);
    high.push(code >>> 5);
    low.push(code & 31);
  }
  return [...high, 0, ...low];
}

const invalidBitcoin = () =>
  new InvalidChainAddressError(
    "bitcoinShape",
    "Enter a valid Bitcoin mainnet address (bc1q…, bc1p…, 1… or 3…).",
  );

/**
 * Validate a mainnet Bitcoin address and return it in canonical form.
 *
 * Both encodings are accepted. Segwit (bech32/bech32m) is what most wallets hand out now, but
 * legacy Base58Check `1…`/`3…` addresses are still in wide circulation and 1Click quotes against
 * them — verified live — so refusing them here would make this stricter than the bridge it guards.
 *
 * Either way the checksum is verified rather than the shape matched, for the same reason EVM
 * addresses assert EIP-55: the address is baked into a quote that can never be edited, so a single
 * transposed character is permanent. A checksum catches that; a regex does not.
 */
function normalizeBitcoinAddress(address: string): string {
  // The prefix picks the encoding, and it has to: a Base58Check decode of a bech32 address fails
  // for the wrong reason, and the two have different canonical forms.
  const prefix = address[0];
  if (prefix === "1" || prefix === "3") return normalizeLegacyBitcoinAddress(address);
  if (address.toLowerCase().startsWith(`${BITCOIN_HRP}1`)) return normalizeSegwitAddress(address);
  throw invalidBitcoin();
}

function normalizeSegwitAddress(address: string): string {
  // Bech32 is case-insensitive but single-case: mixing them is forbidden outright, because the
  // checksum is computed over one casing and a mixed address can no longer assert its own.
  // oxlint-disable-next-line custom/no-raw-address-comparison -- bech32 single-case check, not an Aptos address
  if (address !== address.toLowerCase() && address !== address.toUpperCase())
    throw invalidBitcoin();

  const lowered = address.toLowerCase();
  if (lowered.length > BECH32_MAX_LENGTH) throw invalidBitcoin();

  // Last separator, not the first: `1` is absent from the charset but legal inside the hrp.
  const separator = lowered.lastIndexOf("1");
  if (lowered.slice(0, separator) !== BITCOIN_HRP) throw invalidBitcoin();

  const data: number[] = [];
  for (const char of lowered.slice(separator + 1)) {
    const value = BECH32_CHARSET.indexOf(char);
    if (value < 0) throw invalidBitcoin();
    data.push(value);
  }

  // A witness program is 2-40 bytes, which is 2-64 groups of 5 bits, and the data part carries the
  // version symbol and the checksum on top of it. Checked as a length so `data[0]` below is real.
  if (data.length < 1 + 2 + BECH32_CHECKSUM_LENGTH) throw invalidBitcoin();

  const version = data[0];
  const program = data.slice(1, -BECH32_CHECKSUM_LENGTH);
  if (version === undefined || version > 16 || program.length > 64) throw invalidBitcoin();

  // BIP-350 pins the constant to the witness version: v0 is bech32, v1+ is bech32m. Accepting
  // either would let a wallet encoding the wrong one through, naming a different script.
  const expected = version === 0 ? BECH32_CONST : BECH32M_CONST;
  if (bech32Polymod([...expandHrp(BITCOIN_HRP), ...data]) !== expected) {
    throw new InvalidChainAddressError(
      "bitcoinChecksum",
      "That Bitcoin address failed its bech32 checksum. Re-copy it from your wallet.",
    );
  }

  return lowered;
}

/**
 * Decode a base58 string into its raw bytes, or `undefined` if it is not base58 at all.
 *
 * The Base58Check checksum is not verified here — the caller distinguishes "not an address" from
 * "an address with a typo in it", and those are different messages.
 */
function decodeBase58(input: string, alphabet = BASE58_ALPHABET): Uint8Array | undefined {
  let value = BigInt(0);
  for (const char of input) {
    const digit = alphabet.indexOf(char);
    if (digit < 0) return undefined;
    value = value * BigInt(58) + BigInt(digit);
  }

  const body: number[] = [];
  while (value > BigInt(0)) {
    body.unshift(Number(value & BigInt(0xff)));
    value >>= BigInt(8);
  }

  // Leading zero bytes survive base58 as the alphabet's zero symbol and cannot come back out of
  // the integer above — which matters for both Bitcoin P2PKH (`1`) and XRP classic (`r`).
  let leadingZeros = 0;
  while (leadingZeros < input.length && input[leadingZeros] === alphabet[0]) leadingZeros += 1;

  return Uint8Array.from([...new Array<number>(leadingZeros).fill(0), ...body]);
}

/**
 * Validate a legacy P2PKH or P2SH address and return it unchanged.
 *
 * Unchanged rather than canonicalised: Base58Check is case-sensitive, so there is no equivalent of
 * bech32's case folding — `3J98…` and `3j98…` are different strings over different payloads, and
 * at most one of them is anybody's address.
 */
function normalizeXrpAddress(address: string): string {
  if (!address.startsWith("r")) {
    throw new InvalidChainAddressError(
      "xrpShape",
      "Enter a valid XRP Ledger mainnet classic address (starting with r).",
    );
  }

  const decoded = decodeBase58(address, XRP_BASE58_ALPHABET);
  if (!decoded || decoded.length !== BASE58CHECK_LENGTH || decoded[0] !== 0) {
    throw new InvalidChainAddressError(
      "xrpShape",
      "Enter a valid XRP Ledger mainnet classic address (starting with r).",
    );
  }

  const payload = decoded.subarray(0, BASE58CHECK_LENGTH - BASE58CHECK_CHECKSUM_LENGTH);
  const checksum = decoded.subarray(BASE58CHECK_LENGTH - BASE58CHECK_CHECKSUM_LENGTH);
  const expected = sha256(sha256(payload));
  for (const [i, byte] of checksum.entries()) {
    if (byte !== expected[i]) {
      throw new InvalidChainAddressError(
        "xrpChecksum",
        "That XRP address failed its checksum. Re-copy it from your wallet.",
      );
    }
  }

  return address;
}

function normalizeLegacyBitcoinAddress(address: string): string {
  const decoded = decodeBase58(address);
  if (!decoded || decoded.length !== BASE58CHECK_LENGTH) throw invalidBitcoin();

  // Mainnet only. A testnet address carries a perfectly valid checksum, so the version byte is the
  // only thing saying the funds would land somewhere unspendable.
  const version = decoded[0];
  if (version !== BITCOIN_P2PKH_VERSION && version !== BITCOIN_P2SH_VERSION) throw invalidBitcoin();

  const payload = decoded.subarray(0, BASE58CHECK_LENGTH - BASE58CHECK_CHECKSUM_LENGTH);
  const checksum = decoded.subarray(BASE58CHECK_LENGTH - BASE58CHECK_CHECKSUM_LENGTH);
  const expected = sha256(sha256(payload));

  for (const [i, byte] of checksum.entries()) {
    if (byte !== expected[i]) {
      throw new InvalidChainAddressError(
        "bitcoinChecksum",
        "That Bitcoin address failed its checksum. Re-copy it from your wallet.",
      );
    }
  }

  return address;
}
